/* eslint-disable @khanacademy/ts-no-error-suppressions -- porting from old code */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {staticUrl} from "@repo/urls";

import useProfile from "~/data-access/use-profile.ts";
import {useUpdatePageSettings} from "~/data-access/use-update-page-settings.ts";

import OnboardingAnimation from "./onboarding-animation.tsx";

import xIcon from "@phosphor-icons/core/regular/x.svg";

const descriptionId = "meet-khanmigo-content-id";

interface Props {
    dismiss: () => void;
}

export default function GuideOnboarding(props: Props): React.ReactElement {
    const {dismiss} = props;
    useUpdatePageSettings({title: t`Meet Khanmigo`});
    const {profile} = useProfile();
    const isTeacher = profile?.get("isTeacher") ?? false;

    React.useEffect(() => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideOnboardingVisited",
            eventSchemaVersion: 2,
        });
    }, []);

    const handleGuideSelected = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideOnboardingChatClicked",
            eventSchemaVersion: 2,
        });
        dismiss();
    };

    const handleDashboardIconSelected = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideOnboardingDashboardClicked",
            eventSchemaVersion: 2,
            linkType: "ICON",
        });
        dismiss();
    };

    const handleDashboardCTASelected = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideOnboardingDashboardClicked",
            eventSchemaVersion: 2,
            linkType: "CTA",
        });
        dismiss();
    };

    // If the user has set a homepage URL that isn't "/", send them there.
    // Otherwise, send teachers to teacher dashboard and learners to profile.
    const userHomepageUrl = profile?.get("homepageUrl");
    const dashboardPath =
        userHomepageUrl && userHomepageUrl !== "/"
            ? userHomepageUrl
            : isTeacher
              ? "/teacher/dashboard"
              : "/profile/me";

    return (
        <View
            style={styles.container}
            role="dialog"
            aria-label={t`Meet Khanmigo`}
            aria-describedby={descriptionId}
            testId="onboarding-module"
        >
            {isTeacher ? (
                <TeacherOnboarding id={descriptionId} />
            ) : (
                <OnboardingAnimation id={descriptionId} />
            )}
            <View style={styles.linkContainer}>
                <Button onClick={handleGuideSelected} testId="try-activities">
                    {t`Try the Activities`}
                </Button>
                <Button
                    href={dashboardPath}
                    kind="tertiary"
                    testId="go-to-dashboard"
                    onClick={handleDashboardCTASelected}
                >
                    {t`Go to my dashboard`}
                </Button>
            </View>
            <IconButton
                aria-label={t`Go to my dashboard`}
                icon={xIcon}
                kind="tertiary"
                href={dashboardPath}
                onClick={handleDashboardIconSelected}
                testId="close-onboarding"
                style={styles.closeButton}
            />
        </View>
    );
}

const teacherMediaSharedStyles = {
    borderRadius: spacing.xxxSmall_4,
    height: 405,
    width: 720,
} as const;

const styles = StyleSheet.create({
    closeButton: {
        position: "absolute",
        insetInlineEnd: spacing.small_12,
        top: spacing.small_12,
        // @ts-expect-error - TS2322 - Type '{ position: "absolute"; right: 12; top: 12; "@media (min-width: 768px)": { right: number; top: number; }; }' is not assignable to type 'CSSProperties'.
        "@media (min-width: 768px)": {
            right: 40,
            top: 40,
        },
    },
    container: {
        alignItems: "center",
        paddingBlockEnd: spacing.xxLarge_48,
        paddingBlockStart: spacing.xxLarge_48,
        position: "relative",
    },
    linkContainer: {
        alignItems: "center",
        flexDirection: "column",
        gap: spacing.medium_16,
        marginBlockStart: spacing.xLarge_32,
    },
    teacherIFrame: {
        ...teacherMediaSharedStyles,
    },
    teacherImage: {
        ...teacherMediaSharedStyles,
        backgroundColor: "#29221e", // fallback for image
        backgroundImage: `url(${staticUrl(
            "/images/guide/teacher-video-thumbnail.png",
        )})`,
        backgroundSize: "cover",
        position: "relative",
    },
    teacherPlayButton: {
        bottom: 30,
        insetInlineStart: 30,
        height: 90,
        width: 90,
        position: "absolute",
    },
    teacherPlayButtonFocused: {
        stroke: "white",
        strokeWidth: 4,
    },
});

const TeacherOnboarding = ({id}) => {
    const [showVideo, setShowVideo] = React.useState(false);
    const handleVideoClick = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideOnboardingTeacherVideoClicked",
            eventSchemaVersion: 2,
        });
        setShowVideo(true);
    };
    return (
        <View id={id}>
            {showVideo ? (
                <iframe
                    className={css(styles.teacherIFrame)}
                    src="https://www.youtube.com/embed/M51vnDUsRm4?autoplay=1&rel=0"
                    title="Say hello to Khanmigo!"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    // @ts-expect-error - TS2322 - Type '{ className: string; src: string; title: string; frameBorder: string; allow: string; allowFullScreen: true; autoPlay: boolean; }' is not assignable to type 'DetailedHTMLProps<IframeHTMLAttributes<HTMLIFrameElement>, HTMLIFrameElement>'.
                    autoPlay={true}
                />
            ) : (
                <Clickable
                    aria-label={t`Say hello to Khanmigo!`}
                    onClick={handleVideoClick}
                    style={styles.teacherImage}
                >
                    {({focused, hovered}) => (
                        <svg
                            className={css(styles.teacherPlayButton)}
                            viewBox="0 0 75 75"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                className={css(
                                    (focused || hovered) &&
                                        styles.teacherPlayButtonFocused,
                                )}
                                cx="37.5"
                                cy="37.5"
                                r="35.5"
                                fill="#F46A37"
                            />
                            <path
                                d="M30.21 52.08V27.02V22.91L51.81 37.50L30.21 52.08Z"
                                fill="white"
                            />
                        </svg>
                    )}
                </Clickable>
            )}
        </View>
    );
};
